/**
 * Contact Service
 * Service for managing contact CRUD operations
 */

import { BaseService } from './BaseService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

/**
 * Helper class for working with contacts
 */
export class ContactHelper {
    /**
     * Get full name from contact
     * @param {Object} contact - Contact object
     * @returns {string} Full name
     */
    static getFullName(contact) {
        if (!contact) return '';
        const firstName = contact.firstName || '';
        const lastName = contact.lastName || '';
        return `${firstName} ${lastName}`.trim() || 'Unknown Contact';
    }

    /**
     * Format address for display
     * @param {Object} contact - Contact object
     * @returns {string} Formatted address
     */
    static formatAddress(contact) {
        if (!contact) return '';
        
        const parts = [];
        
        if (contact.address1) parts.push(contact.address1);
        if (contact.address2) parts.push(contact.address2);
        
        const cityStateZip = [];
        if (contact.city) cityStateZip.push(contact.city);
        if (contact.stateCode) cityStateZip.push(contact.stateCode);
        if (contact.zip) cityStateZip.push(contact.zip);
        
        if (cityStateZip.length > 0) {
            parts.push(cityStateZip.join(' '));
        }
        
        return parts.join(', ') || 'No address';
    }

    /**
     * Validate contact data
     * @param {Object} contact - Contact object to validate
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    static validateContact(contact) {
        const errors = [];
        
        // Required: firstName
        if (!contact.firstName || contact.firstName.trim() === '') {
            errors.push('First name is required');
        } else if (contact.firstName.length > 50) {
            errors.push('First name must not exceed 50 characters');
        }
        
        // Optional: lastName (only validate if not null)
        if (contact.lastName !== null && contact.lastName !== undefined && contact.lastName.length > 50) {
            errors.push('Last name must not exceed 50 characters');
        }
        
        // Optional: email (only validate if not null)
        if (contact.email !== null && contact.email !== undefined) {
            if (contact.email.length > 254) {
                errors.push('Email must not exceed 254 characters');
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (contact.email && !emailRegex.test(contact.email)) {
                errors.push('Email format is invalid');
            }
        }
        
        // Optional: stateCode (only validate if not null)
        if (contact.stateCode !== null && contact.stateCode !== undefined && contact.stateCode.length !== 2) {
            errors.push('State code must be exactly 2 characters (e.g., NY, CA)');
        }
        
        // Address fields (only validate if not null)
        const addressFields = ['address1', 'address2', 'city'];
        addressFields.forEach(field => {
            if (contact[field] !== null && contact[field] !== undefined && contact[field].length > 100) {
                errors.push(`${field} must not exceed 100 characters`);
            }
        });
        
        // Zip (only validate if not null)
        if (contact.zip !== null && contact.zip !== undefined && contact.zip.length > 20) {
            errors.push('ZIP code must not exceed 20 characters');
        }
        
        return {
            valid: errors.length === 0,
            errors
        };
    }

    /**
     * Create contact DTO for API
     * @param {Object} contactData - Contact form data
     * @returns {Object} Contact DTO
     */
    static createContactDto(contactData) {
        const trimmedFirstName = contactData.firstName?.trim() || '';
        const trimmedLastName = contactData.lastName?.trim();
        const trimmedEmail = contactData.email?.trim();
        const trimmedPhone = contactData.phone?.trim();
        const trimmedAddress1 = contactData.address1?.trim();
        const trimmedAddress2 = contactData.address2?.trim();
        const trimmedCity = contactData.city?.trim();
        const trimmedStateCode = contactData.stateCode?.trim()?.toUpperCase();
        const trimmedZip = contactData.zip?.trim();
        
        const dto = {
            firstName: trimmedFirstName,
            isSubscribed: contactData.isSubscribed !== undefined ? contactData.isSubscribed : true,
            clientPortalAccess: contactData.clientPortalAccess !== undefined ? contactData.clientPortalAccess : false,
            isActive: contactData.isActive !== undefined ? contactData.isActive : true
        };
        
        // Only include optional fields if they have values (non-empty strings)
        if (trimmedLastName && trimmedLastName !== '') dto.lastName = trimmedLastName;
        if (trimmedEmail && trimmedEmail !== '') dto.email = trimmedEmail;
        if (trimmedPhone && trimmedPhone !== '') dto.phone = trimmedPhone;
        if (trimmedAddress1 && trimmedAddress1 !== '') dto.address1 = trimmedAddress1;
        if (trimmedAddress2 && trimmedAddress2 !== '') dto.address2 = trimmedAddress2;
        if (trimmedCity && trimmedCity !== '') dto.city = trimmedCity;
        if (trimmedStateCode && trimmedStateCode !== '') dto.stateCode = trimmedStateCode;
        if (trimmedZip && trimmedZip !== '') dto.zip = trimmedZip;
        
        return dto;
    }
}

/**
 * Contact Service Class
 */
export class ContactService extends BaseService {
    constructor() {
        super('/api/contacts', ['createdAt', 'lastModifiedAt']);
    }

    /**
     * Get all contacts
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of contacts
     */
    async getAllContacts(accessToken) {
        try {
            return await this.makeRequest(accessToken, '');
        } catch (error) {
            console.error('Failed to fetch all contacts:', error);
            throw error;
        }
    }

    /**
     * Get active contacts only
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of active contacts
     */
    async getActiveContacts(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/active');
        } catch (error) {
            console.error('Failed to fetch active contacts:', error);
            throw error;
        }
    }

    /**
     * Get contact by ID
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<Object|null>} Contact object or null if not found
     */
    async getContactById(accessToken, contactId) {
        try {
            return await this.makeRequest(accessToken, `/${contactId}`);
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error(`Failed to fetch contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts by email
     * @param {string} accessToken - JWT access token
     * @param {string} email - Email address
     * @returns {Promise<Array>} Array of contacts with this email
     */
    async getContactsByEmail(accessToken, email) {
        try {
            return await this.makeRequest(accessToken, `/email/${encodeURIComponent(email)}`);
        } catch (error) {
            console.error(`Failed to fetch contacts by email ${email}:`, error);
            throw error;
        }
    }

    /**
     * Search contacts by last name
     * @param {string} accessToken - JWT access token
     * @param {string} lastName - Last name to search
     * @returns {Promise<Array>} Array of matching contacts
     */
    async searchByLastName(accessToken, lastName) {
        try {
            return await this.makeRequest(accessToken, `/search/lastname/${encodeURIComponent(lastName)}`);
        } catch (error) {
            console.error(`Failed to search by last name ${lastName}:`, error);
            throw error;
        }
    }

    /**
     * Search contacts by first name
     * @param {string} accessToken - JWT access token
     * @param {string} firstName - First name to search
     * @returns {Promise<Array>} Array of matching contacts
     */
    async searchByFirstName(accessToken, firstName) {
        try {
            return await this.makeRequest(accessToken, `/search/firstname/${encodeURIComponent(firstName)}`);
        } catch (error) {
            console.error(`Failed to search by first name ${firstName}:`, error);
            throw error;
        }
    }

    /**
     * Create new contact
     * @param {string} accessToken - JWT access token
     * @param {Object} contactData - Contact data
     * @returns {Promise<Object>} Created contact
     */
    async createContact(accessToken, contactData) {
        try {
            const dto = ContactHelper.createContactDto(contactData);
            
            // Validate before sending
            const validation = ContactHelper.validateContact(dto);
            if (!validation.valid) {
                throw new Error(validation.errors.join(', '));
            }

            return await this.makeRequest(accessToken, '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dto)
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update contact
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @param {Object} contactData - Updated contact data
     * @returns {Promise<Object>} Updated contact
     */
    async updateContact(accessToken, contactId, contactData) {
        try {
            const dto = ContactHelper.createContactDto(contactData);
            
            // Validate before sending
            const validation = ContactHelper.validateContact(dto);
            if (!validation.valid) {
                throw new Error(validation.errors.join(', '));
            }

            return await this.makeRequest(accessToken, `/${contactId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dto)
            });
        } catch (error) {
            console.error(`Failed to update contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Activate contact (sets isActive to true)
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<Object>} Updated contact object
     */
    async activateContact(accessToken, contactId) {
        try {
            return await this.makeRequest(accessToken, `/${contactId}/activate`, {
                method: 'PATCH'
            });
        } catch (error) {
            // If activate endpoint doesn't exist (404), fall back to general update
            if (error.message && error.message.includes('404')) {
                // Get current contact data
                const currentContact = await this.getContactById(accessToken, contactId);
                if (!currentContact) {
                    throw new Error('Contact not found');
                }
                
                // Update with isActive: true
                return await this.updateContact(accessToken, contactId, {
                    ...currentContact,
                    isActive: true
                });
            }
            
            console.error(`Failed to activate contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Deactivate contact (soft delete)
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<Object>} Updated contact object
     */
    async deactivateContact(accessToken, contactId) {
        try {
            return await this.makeRequest(accessToken, `/${contactId}/deactivate`, {
                method: 'PATCH'
            });
        } catch (error) {
            // If deactivate endpoint doesn't exist (404), fall back to general update
            if (error.message && error.message.includes('404')) {
                // Get current contact data
                const currentContact = await this.getContactById(accessToken, contactId);
                if (!currentContact) {
                    throw new Error('Contact not found');
                }
                
                // Update with isActive: false
                return await this.updateContact(accessToken, contactId, {
                    ...currentContact,
                    isActive: false
                });
            }
            
            console.error(`Failed to deactivate contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Delete contact (hard delete)
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteContact(accessToken, contactId) {
        try {
            await this.makeRequest(accessToken, `/${contactId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error(`Failed to delete contact ${contactId}:`, error);
            throw error;
        }
    }
}

// Create singleton instance
const contactService = new ContactService();

// Export both named and default for flexibility
export { contactService };
export default contactService;
